import { StandardEvents } from '@shopify/events';

/**
 * Swaps a server-rendered region with its fresh copy when the variant changes.
 *
 * Markup contract: `<variant-region data-region-id="unique-id">…</variant-region>`.
 * Horizon's variant picker fetches the whole product section for the new variant
 * and passes the parsed document on the ProductSelectEvent promise
 * (`detail.html`). This element finds the element with the same region id in that
 * document and copies its children in, so prices and labels stay rendered by
 * Liquid. Put `aria-live` on an inner element so the swap is announced once.
 */
class VariantRegion extends HTMLElement {
  /** @type {Element | null} */
  #target = null;

  connectedCallback() {
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#target = null;
  }

  /**
   * @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event
   */
  #handleProductSelect = async (event) => {
    if (!event.promise) return;

    try {
      const result = await event.promise;
      const html = result?.detail?.html;
      if (!(html instanceof Document)) return;

      const regionId = this.dataset.regionId;
      if (!regionId) return;

      const fresh = html.querySelector(`variant-region[data-region-id="${CSS.escape(regionId)}"]`);
      if (!fresh) return;
      if (fresh.innerHTML === this.innerHTML) return;

      // Keep the live region element in place and update only its content, so
      // screen readers announce the change once.
      const liveTarget = this.querySelector('[aria-live]');
      const liveFresh = fresh.querySelector('[aria-live]');
      if (liveTarget && liveFresh && liveTarget.parentElement === this && fresh.children.length === this.children.length) {
        for (const [index, child] of [...fresh.children].entries()) {
          const current = this.children[index];
          if (!current) continue;
          if (current === liveTarget) {
            if (current.innerHTML !== child.innerHTML) current.innerHTML = child.innerHTML;
          } else if (current.outerHTML !== child.outerHTML) {
            current.replaceWith(child.cloneNode(true));
          }
        }
        return;
      }

      this.replaceChildren(...[...fresh.childNodes].map((node) => node.cloneNode(true)));
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[variant-region] Could not refresh region:', error);
    }
  };
}

if (!customElements.get('variant-region')) {
  customElements.define('variant-region', VariantRegion);
}
